import React, { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  startSession,
  stopSession,
} from "../../store/slices/telecallerSessionSlice";
import { RootDispatch, RootState } from "../../store";
import { Link, NavLink, useNavigate } from "react-router-dom";
import logo from "../../assets/images/Logo.png";

import { setCurrentLead } from "../../store/slices/telecallerLeadSlice";
import useOutsideClick from "../Assets/useOutsideClick";
import { logOutUser } from "../../store/slices/authSlice";
import { getUserId } from "../../utils/auth";
import {
  getRemarks,
  searchTelecallerLeads,
} from "../../store/slices/telecallerLeadSlice";
import TaskManagerNotificationBell from "../TaskManagement/TaskManagerNotificationBell";

const TelecallerNavbar: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();

  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showResults, setShowResults] = useState(false);

  const sessionActive = useSelector(
    (state: RootState) => state.telecallerSession.active
  );
  const { userLogsCount } = useSelector(
    (state: RootState) => state.telecallerLead
  );
  const loading = useSelector(
    (state: RootState) =>
      state.telecallerLead.loadingAndErrorState.fetchingTelecallerLead
  );

  const navigate = useNavigate();

  const userName = useSelector((state: RootState) => state.auth.userName);
  const role = useSelector((state: RootState) => state.auth.role);
  const userId = getUserId();

  const searchRef = useRef<HTMLDivElement>(null);
  useOutsideClick(searchRef, () => setShowResults(false));

  const handleSearch = async (query: string) => {
    if (query.length > 2) {
      const results = await dispatch(
        searchTelecallerLeads({ query, userId: userId ?? "" })
      ).unwrap();
      setSearchResults(results);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const handleSelectLead = (lead: any) => {
    dispatch(setCurrentLead(lead));
    dispatch(getRemarks(lead._id));
    dispatch(startSession());
    setShowResults(false);
  };

  const toggleSession = () => {
    dispatch(sessionActive ? stopSession() : startSession());
  };

  const handleLogout = async () => {
    await dispatch(logOutUser());
    await navigate("/login");
  };

  return (
    <div className="w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white py-2 px-4 flex justify-between items-center">
      <div className="flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2">
          <img src={logo} alt="Frelit" className="w-6 h-6" />
          <span className="font-semibold">Frelit</span>
        </Link>
        <NavLink
          to="/"
          className="px-3 py-1 rounded-md hover:bg-blue-500 hover:text-white transition-all"
        >
          Task Manager
        </NavLink>
        <NavLink
          to="/lead"
          className="px-3 py-1 rounded-md hover:bg-blue-500 hover:text-white transition-all"
        >
          Leads
        </NavLink>
        <NavLink
          to="/importExcel"
          className="px-3 py-1 rounded-md hover:bg-blue-500 hover:text-white transition-all"
        >
          Import File
        </NavLink>

        <div className="relative" ref={searchRef}>
          <div className="flex items-center bg-white rounded-md px-3 py-1">
            <input
              type="text"
              placeholder="Search leads..."
              className="text-gray-800 bg-transparent border-none focus:outline-none w-64"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                handleSearch(e.target.value);
              }}
              onFocus={() => searchQuery.length > 2 && setShowResults(true)}
            />
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          {/* Search Results Dropdown */}
          {showResults && searchResults.length > 0 && (
            <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg max-h-80 overflow-y-auto">
              {searchResults.map((lead) => (
                <div
                  key={lead._id}
                  className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                  onClick={() => handleSelectLead(lead)}
                >
                  <div className="font-medium text-gray-800">{lead.name}</div>
                  <div className="text-sm text-gray-600">
                    Mobile: {lead.mobileNumber} |
                    {lead.whatsappNumber && ` WhatsApp: ${lead.whatsappNumber}`}
                  </div>
                  <div className="text-xs text-gray-500">
                    {lead.address?.state}, {lead.address?.district}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-6">
        <div className="bg-white rounded-md shadow-md py-2 px-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex gap-2 items-center rounded-lg">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <h3 className="text-sm font-medium text-gray-500">
                Total Calls:
              </h3>
            </div>
            <div className="flex gap-4 ">
              <p className="text-sm font-bold text-gray-800">
                {loading ? (
                  <span className="inline-block h-6 w-6 animate-pulse rounded-full bg-gray-200"></span>
                ) : (
                  userLogsCount
                )}
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={toggleSession}
          className={`px-3 py-1 rounded-md font-medium transition-all ${
            sessionActive
              ? "bg-red-600 hover:bg-red-700"
              : "bg-green-600 hover:bg-green-700"
          }`}
        >
          {sessionActive ? "Stop Session" : "Start Session"}
        </button>
        <TaskManagerNotificationBell />
        <div ref={userDropdownRef} className="relative">
          <button
            onClick={() => setIsUserDropdownOpen((v) => !v)}
            className="text-sm font-medium text-gray-700 flex items-center gap-1"
          >
            <span
              className={`text-sm font-medium ${
                role === "telecaller" ? "text-white" : "text-gray-700"
              }`}
            >
              Welcome!,&nbsp;
              <span
                className={` ${
                  role === "telecaller" ? "text-white" : "text-blue-600"
                }`}
              >
                {userName?.username.capitalizeWords()}
              </span>
            </span>
            <span
              className={`${isUserDropdownOpen ? "rotate-180" : ""} ${
                role === "telecaller" ? "text-white" : "text-gray-700"
              }`}
            >
              ▼
            </span>
          </button>

          {isUserDropdownOpen && (
            <ul
              className={`absolute right-0 mt-2 w-32 bg-white border rounded shadow-lg z-50 ${
                role === "telecaller" ? "text-black" : ""
              }`}
            >
              <li
                onClick={handleLogout}
                className="px-4 py-2 hover:bg-red-600 hover:text-white cursor-pointer"
              >
                Logout
              </li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default TelecallerNavbar;
